import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AdminLayout from "../../layout/AdminLayoutSewa";
import Swal from "sweetalert2";
import {
  adminGetAgents,
  adminGetAgentUnits,
  adminListCustomers,
  adminListZones,
  adminSetAgentUnits,
} from "../../api";

export default function AgentUnitAssignmentPage() {
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [agents, setAgents] = useState([]);

  const [agentId, setAgentId] = useState(searchParams.get("agentId") || "");
  const [template, setTemplate] = useState(searchParams.get("template") || "penjaja");
  const [kawasan, setKawasan] = useState("");
  const [search, setSearch] = useState("");

  const [kawasanList, setKawasanList] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [assigned, setAssigned] = useState([]);
  const [selected, setSelected] = useState({});

  const loadAgents = async () => {
    const res = await adminGetAgents();
    setAgents(Array.isArray(res) ? res : []);
  };

  const loadKawasans = async () => {
    const res = await adminListZones(template);
    const zones = Array.isArray(res) ? res : [];

    const allKawasans = zones.flatMap((z) =>
      Array.isArray(z.kawasans) ? z.kawasans : []
    );

    const normalized = allKawasans.map((k) => ({
      kawasan_id: k.kawasan_id || k.kawasan_name || "",
      kawasan_name: k.kawasan_name || k.kawasan_id || "",
      total_units: k.total_units || 0,
    }));

    normalized.sort((a, b) => a.kawasan_name.localeCompare(b.kawasan_name));
    setKawasanList(normalized);
  };

 const loadCustomers = async () => {
  const params = { template };

  if (kawasan) params.kawasan = kawasan;
  if (search) params.search = search;

  const res = await adminListCustomers(params);
  const rows = Array.isArray(res) ? res : [];

  const filtered = kawasan
    ? rows.filter((x) => String(x.kawasan || "").trim() === String(kawasan).trim())
    : rows;

  setCustomers(filtered);
};

  const loadAssigned = async () => {
    if (!agentId) {
      setAssigned([]);
      return;
    }
    const res = await adminGetAgentUnits(agentId, template);
    setAssigned(Array.isArray(res) ? res : []);
  };

  const refreshAll = async () => {
    try {
      setLoading(true);
      await Promise.all([loadAgents(), loadKawasans(), loadAssigned()]);
      await loadCustomers();
    } catch (e) {
      console.error(e);
      await Swal.fire({
        icon: "error",
        title: "Gagal",
        text: e?.response?.data?.error || "Gagal load assignment page.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAgents().catch(console.error);
  }, []);

  useEffect(() => {
    setKawasan("");
    loadKawasans().catch(console.error);
  }, [template]);

  useEffect(() => {
    loadCustomers().catch(console.error);
  }, [template, kawasan, search]);

  useEffect(() => {
    loadAssigned().catch(console.error);
  }, [agentId, template]);

  useEffect(() => {
    const map = {};
    assigned.forEach((x) => {
      const noAkaun = x.no_akaun || x.noAkaun;
      if (noAkaun) map[noAkaun] = true;
    });
    setSelected(map);
  }, [assigned]);

  const toggleAccount = (noAkaun) => {
    setSelected((prev) => ({
      ...prev,
      [noAkaun]: !prev[noAkaun],
    }));
  };

  const selectedCount = useMemo(
    () => Object.values(selected).filter(Boolean).length,
    [selected]
  );

  const handleSave = async () => {
    if (!agentId) {
      return Swal.fire({
        icon: "warning",
        title: "Pilih ejen",
        text: "Sila pilih ejen terlebih dahulu.",
      });
    }

    try {
      setLoading(true);

      const units = Object.entries(selected)
        .filter(([, checked]) => checked)
        .map(([noAkaun]) => noAkaun);

      const res = await adminSetAgentUnits(agentId, {
        template,
        units,
      });

      await Swal.fire({
        icon: "success",
        title: "Berjaya",
        text: `Jumlah unit ditugaskan: ${res?.total_assigned ?? 0}`,
      });

      await loadAssigned();
    } catch (e) {
      console.error(e);
      await Swal.fire({
        icon: "error",
        title: "Gagal",
        text: e?.response?.data?.error || "Gagal simpan tugasan unit.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto bg-white shadow-md rounded-lg p-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Tugasan Unit Ejen</h2>
            <p className="text-xs text-gray-500 mt-1">
              Admin tetapkan unit ikut no akaun. Pilih template dan kawasan dahulu.
            </p>
          </div>

          <button
            onClick={refreshAll}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
          >
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="text-xs text-gray-600">Ejen</label>
            <select
              className="border rounded-md w-full p-2 text-sm bg-white"
              value={agentId}
              onChange={(e) => setAgentId(e.target.value)}
            >
              <option value="">Pilih ejen</option>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name || a.login_id || `Agent ${a.id}`}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs text-gray-600">Template</label>
            <select
              className="border rounded-md w-full p-2 text-sm bg-white"
              value={template}
              onChange={(e) => setTemplate(e.target.value)}
            >
              <option value="penjaja">Penjaja</option>
              <option value="perumahan">Perumahan</option>
            </select>
          </div>

          <div>
            <label className="text-xs text-gray-600">Kawasan</label>
            <select
              className="border rounded-md w-full p-2 text-sm bg-white"
              value={kawasan}
              onChange={(e) => setKawasan(e.target.value)}
            >
              <option value="">Semua Kawasan</option>
              {kawasanList.map((k) => (
                <option key={k.kawasan_id} value={k.kawasan_name}>
                  {k.kawasan_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs text-gray-600">Search Customer / Unit / Akaun</label>
            <input
              className="border rounded-md w-full p-2 text-sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="No akaun / bil / unit / kawasan"
            />
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Dipilih: <span className="font-semibold">{selectedCount}</span>
            {kawasan ? (
              <span className="ml-3 text-gray-500">Kawasan: {kawasan}</span>
            ) : null}
          </div>

          <button
            onClick={handleSave}
            className="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700"
            disabled={loading || !agentId}
          >
            Simpan Tugasan
          </button>
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm border">
            <thead className="bg-gray-50">
              <tr>
                <th className="p-2 border text-left">Pilih</th>
                <th className="p-2 border text-left">No Akaun</th>
                <th className="p-2 border text-left">BIL</th>
                <th className="p-2 border text-left">Unit</th>
                <th className="p-2 border text-left">Kawasan</th>
                <th className="p-2 border text-left">Telefon</th>
              </tr>
            </thead>

            <tbody>
              {customers.length === 0 ? (
                <tr>
                  <td colSpan="6" className="p-3 text-center text-gray-500">
                    Tiada data.
                  </td>
                </tr>
              ) : (
                customers.map((c) => {
                  const noAkaun = c.no_akaun || c.nomborAkaun || c.noAkaun;

                  return (
                    <tr key={noAkaun} className="border-t hover:bg-gray-50">
                      <td className="p-2 border">
                        <input
                          type="checkbox"
                          checked={!!selected[noAkaun]}
                          onChange={() => toggleAccount(noAkaun)}
                        />
                      </td>
                      <td className="p-2 border font-semibold">{noAkaun}</td>
                      <td className="p-2 border">{c.bil || "-"}</td>
                      <td className="p-2 border">{c.nombor_unit || c.nomborUnit || "-"}</td>
                      <td className="p-2 border">{c.kawasan || "-"}</td>
                      <td className="p-2 border">{c.telefon || "-"}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}